<script setup>
import { useAttrs, ref, onMounted, onBeforeUnmount, nextTick } from 'vue';

defineOptions({ inheritAttrs: false });

const props = defineProps({
  videoSrc: { type: String, default: '/brand-assets/crowork.webm' },
  imgSrc: { type: String, default: '/brand-assets/crow-logo.png' },
  videoScale: { type: Number, default: 1.35 },
});

// Fallback duration if metadata is unavailable.
const FALLBACK_DURATION_MS = 20000;
// Custom event other parts of the app can dispatch to trigger the animation.
const PLAY_EVENT = 'crow-logo-play';

const attrs = useAttrs();
const altText = () => (typeof attrs.alt === 'string' ? attrs.alt : '');
const videoEl = ref(null);
const showVideo = ref(false);

let timeoutId = null;
let hourTimerId = null;
let isPlaying = false;

const stopAndShowImg = () => {
  const v = videoEl.value;
  if (v) {
    try {
      v.pause();
      v.currentTime = 0;
    } catch (e) {
      /* noop */
    }
  }
  showVideo.value = false;
  isPlaying = false;
};

const playOnce = () => {
  if (isPlaying) return;
  isPlaying = true;
  showVideo.value = true;
  nextTick(() => {
    const v = videoEl.value;
    if (!v) {
      isPlaying = false;
      showVideo.value = false;
      return;
    }
    try {
      v.currentTime = 0;
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch (e) {
      /* noop */
    }
    if (timeoutId) clearTimeout(timeoutId);
    const durMs =
      v.duration && Number.isFinite(v.duration) && v.duration > 0
        ? v.duration * 1000 + 50
        : FALLBACK_DURATION_MS;
    timeoutId = setTimeout(() => {
      stopAndShowImg();
    }, durMs);
  });
};

const onVideoEnded = () => {
  if (timeoutId) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
  stopAndShowImg();
};

const onPlayEvent = () => playOnce();

// Schedule next firing at the next top of the hour, then every hour after.
const scheduleHourlyTrigger = () => {
  const now = new Date();
  const next = new Date(now);
  next.setHours(now.getHours() + 1, 0, 0, 0);
  const msUntilNextHour = next.getTime() - now.getTime();
  hourTimerId = setTimeout(() => {
    playOnce();
    hourTimerId = setInterval(() => {
      playOnce();
    }, 60 * 60 * 1000);
  }, msUntilNextHour);
};

const clearHourlyTrigger = () => {
  if (hourTimerId) {
    clearTimeout(hourTimerId);
    clearInterval(hourTimerId);
    hourTimerId = null;
  }
};

onMounted(() => {
  window.addEventListener(PLAY_EVENT, onPlayEvent);
  scheduleHourlyTrigger();
});

onBeforeUnmount(() => {
  window.removeEventListener(PLAY_EVENT, onPlayEvent);
  clearHourlyTrigger();
  if (timeoutId) clearTimeout(timeoutId);
});
</script>

<template>
  <!--
    Wrapper recebe todos os attrs do pai (class, style, alt…).
    Img e vídeo se sobrepõem; a troca é feita com opacity para que o
    corvo nunca desapareça — ele simplesmente "ganha vida" no lugar.
  -->
  <div v-bind="attrs" style="position: relative; overflow: hidden;">
    <img
      :src="props.imgSrc"
      :alt="altText()"
      style="
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
        transition: opacity 0.25s ease;
      "
      :style="{ opacity: showVideo ? 0 : 1 }"
    />
    <!-- Vídeo sempre no DOM para pré-carregamento (preload="auto"). -->
    <!-- É posicionado em cima da img e revela-se via opacity. -->
    <video
      ref="videoEl"
      :src="props.videoSrc"
      muted
      playsinline
      preload="auto"
      style="
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
        transition: opacity 0.25s ease;
      "
      :style="{
        opacity: showVideo ? 1 : 0,
        pointerEvents: showVideo ? 'auto' : 'none',
        transform: `scale(${props.videoScale})`,
        transformOrigin: 'center center',
      }"
      @ended="onVideoEnded"
    />
  </div>
</template>
